export interface AgentMeta {
  id: string;
  version: number;
  name: string | null;
  description: string | null;
  model: string | null;
  model_priority: string[] | null;
  reasoning_effort?: "high" | null;
  temperature: number | null;
  user_template: string | null;
  response_format: string | null;
  json_schema: Record<string, unknown> | null;
  is_active: boolean;
  created_at: string;
  has_encrypted_system: boolean;
}

export interface AgentDetail extends AgentMeta {
  system: string | null;
}

export interface AgentUpsert {
  name?: string;
  description?: string;
  model?: string;
  model_priority?: string[];
  temperature?: number;
  system?: string;
  user_template?: string;
  response_format?: string;
  json_schema?: Record<string, unknown>;
}

function baseUrl(): string {
  return `${import.meta.env.VITE_SUPABASE_URL as string}/functions/v1/admin-agents`;
}

async function request<T>(
  method: string,
  path: string,
  token: string,
  body?: unknown,
): Promise<T> {
  const res = await fetch(`${baseUrl()}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error ?? "admin_agents_error");
  }
  return res.json() as Promise<T>;
}

export const adminAgentsApi = {
  list: (token: string) =>
    request<AgentMeta[]>("GET", "", token),

  get: (id: string, token: string) =>
    request<AgentDetail>("GET", `/${id}`, token),

  create: (id: string, data: AgentUpsert & { id?: string }, token: string) =>
    request<AgentMeta>("POST", "", token, { id, ...data }),

  update: (id: string, data: AgentUpsert, token: string) =>
    request<AgentMeta>("PUT", `/${id}`, token, data),

  deactivate: (id: string, token: string) =>
    request<{ id: string; deactivated: boolean }>("DELETE", `/${id}`, token),
};
